import { PLATFORM_INSTITUTION_CODE } from "@eduleo/shared";
import { ArrowRight, CalendarCheck, CreditCard, MessageSquare, Users } from "lucide-react";
import { Link, Navigate } from "react-router";
import { roleLabel } from "../../components/layout/user-menu";
import { Card, Skeleton } from "../../components/ui/feedback";
import { cn } from "../../lib/cn";
import { firstName, formatDate, greeting } from "../../lib/format";
import { useAuthStore } from "../../stores/auth-store";
import { useMe } from "../auth/auth-api";

/** Modules on the roadmap, shown so people know what's coming. Not links. */
const upcoming = [
  { label: "Attendance", icon: CalendarCheck, tint: "bg-attendance-soft text-attendance-fg" },
  { label: "Fees", icon: CreditCard, tint: "bg-fees-soft text-fees-fg" },
  { label: "Messages", icon: MessageSquare, tint: "bg-comms-soft text-comms-fg" },
];

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const me = useMe();
  if (!user) return null;
  // Eduleo's own team works in the platform console, not a school's home page.
  if (user.institution.code === PLATFORM_INSTITUTION_CODE || me.data?.capabilities.platform) {
    return <Navigate to="/platform" replace />;
  }

  const canSeeStudents = me.data?.capabilities.students.list;

  return (
    <div className="flex flex-col gap-5">
      <section className="gradient-hero rounded-xl border border-edge px-5 py-6 md:px-8 md:py-8">
        <p className="font-semibold text-ink-3 text-sm">{formatDate(new Date())}</p>
        <h1 className="mt-1 font-extrabold text-2xl md:text-3xl">
          {greeting()}, {firstName(user.displayName)} 👋
        </h1>
        <p className="mt-2 max-w-xl text-ink-2 text-lg">
          You're signed in as <strong className="text-brand-fg">{roleLabel(user.roles)}</strong> at{" "}
          {user.institution.name}.
        </p>
      </section>

      <h2 className="font-bold text-lg">Go to</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {me.isPending ? (
          <Skeleton className="h-[104px] rounded-lg" />
        ) : canSeeStudents ? (
          <Link
            to="/students"
            className="group flex flex-col gap-3 rounded-lg border border-edge bg-surface p-4 shadow-xs transition hover:-translate-y-px hover:shadow-md"
          >
            <span className="grid size-10 place-items-center rounded-xl bg-classes-soft text-classes-fg">
              <Users aria-hidden className="size-5" strokeWidth={1.5} />
            </span>
            <span className="flex items-center justify-between font-semibold">
              Students
              <ArrowRight
                aria-hidden
                className="size-4 text-ink-3 group-hover:text-brand-fg"
                strokeWidth={1.5}
              />
            </span>
          </Link>
        ) : null}
        {upcoming.map(({ label, icon: Icon, tint }) => (
          <div
            key={label}
            className="flex flex-col gap-3 rounded-lg border border-edge border-dashed bg-surface/60 p-4"
          >
            <span className={cn("grid size-10 place-items-center rounded-xl", tint)}>
              <Icon aria-hidden className="size-5" strokeWidth={1.5} />
            </span>
            <span className="flex flex-col">
              <span className="font-semibold text-ink-2">{label}</span>
              <span className="text-ink-3 text-xs">Coming soon</span>
            </span>
          </div>
        ))}
      </div>

      <Card className="p-5">
        <h2 className="font-bold">Need something changed?</h2>
        <p className="mt-1 text-ink-2">
          Your school admin manages who can see and do what. Ask them if a menu item you need is
          missing.
        </p>
      </Card>
    </div>
  );
}
